import React, { useState, useMemo } from 'react';
import { X, Plus, Package, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { createProduct, batchCreateProducts, parseBatchInput } from '../../api/wholesale/companyProducts';

const AddCompanyProductModal = ({
  isOpen,
  onClose,
  companyId,
  sizeCategories = [],
  tileTypes = [],
  onSuccess
}) => {
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'batch'
  const [loading, setLoading] = useState(false);
  
  // Single entry form
  const [singleForm, setSingleForm] = useState({
    tile_code: '',
    size_category_id: '',
    tile_type_id: '',
    name: '',
    description: '',
    base_price: ''
  });
  
  // Batch entry form
  const [batchForm, setBatchForm] = useState({
    raw_input: '',
    size_category_id: '',
    tile_type_id: ''
  });
  
  // Batch preview
  const [batchResult, setBatchResult] = useState(null);

  // Filter tile types by selected size category
  const filteredTileTypes = useMemo(() => {
    const selectedSizeId = activeTab === 'single' 
      ? parseInt(singleForm.size_category_id) 
      : parseInt(batchForm.size_category_id);
    
    if (!selectedSizeId) return [];
    
    return tileTypes.filter(
      t => t.size_category_id === selectedSizeId || !t.size_category_id
    );
  }, [tileTypes, singleForm.size_category_id, batchForm.size_category_id, activeTab]);

  // Parse batch input preview
  const parsedCodes = useMemo(() => {
    if (!batchForm.raw_input.trim()) return [];
    return parseBatchInput(batchForm.raw_input);
  }, [batchForm.raw_input]);

  const resetForms = () => {
    setSingleForm({
      tile_code: '',
      size_category_id: '',
      tile_type_id: '',
      name: '',
      description: '',
      base_price: ''
    });
    setBatchForm({
      raw_input: '',
      size_category_id: '',
      tile_type_id: ''
    });
    setBatchResult(null);
  };

  const handleClose = () => {
    resetForms();
    onClose();
  };

  // Handle single product creation
  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    
    if (!singleForm.tile_code.trim()) {
      toast.error('Tile code is required');
      return;
    }
    if (!singleForm.size_category_id) {
      toast.error('Size category is required');
      return;
    }
    
    setLoading(true);
    try {
      await createProduct(companyId, {
        tile_code: singleForm.tile_code.trim(),
        size_category_id: parseInt(singleForm.size_category_id),
        tile_type_id: singleForm.tile_type_id ? parseInt(singleForm.tile_type_id) : null,
        name: singleForm.name || null,
        description: singleForm.description || null,
        base_price: singleForm.base_price ? parseFloat(singleForm.base_price) : null
      });
      
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  // Handle batch product creation
  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    
    if (parsedCodes.length === 0) {
      toast.error('No valid tile codes found');
      return;
    }
    if (!batchForm.size_category_id) {
      toast.error('Size category is required');
      return;
    }
    
    setLoading(true);
    try {
      const result = await batchCreateProducts(companyId, {
        tile_codes: parsedCodes,
        size_category_id: parseInt(batchForm.size_category_id),
        tile_type_id: batchForm.tile_type_id ? parseInt(batchForm.tile_type_id) : null
      });
      
      setBatchResult(result);
      
      if (result.created > 0) {
        onSuccess?.();
      }
      
      if (result.errors?.length === 0) {
        handleClose();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to batch create products');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />
        
        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl transform transition-all sm:max-w-lg w-full mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Add Product</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('single')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'single'
                  ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" />
                Single Entry
              </div>
            </button>
            <button
              onClick={() => setActiveTab('batch')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'batch'
                  ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <FileText className="w-4 h-4" />
                Batch Entry
              </div>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {activeTab === 'single' ? (
              /* Single Entry Form */
              <form onSubmit={handleSingleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tile Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={singleForm.tile_code}
                    onChange={(e) => setSingleForm(f => ({ ...f, tile_code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="e.g., 12201, 44VT003"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Size Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={singleForm.size_category_id}
                      onChange={(e) => setSingleForm(f => ({ ...f, size_category_id: e.target.value, tile_type_id: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      required
                    >
                      <option value="">Select size</option>
                      {sizeCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tile Type
                    </label>
                    <select
                      value={singleForm.tile_type_id}
                      onChange={(e) => setSingleForm(f => ({ ...f, tile_type_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={!singleForm.size_category_id}
                    >
                      <option value="">{singleForm.size_category_id ? 'Select type' : 'Select size first'}</option>
                      {filteredTileTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={singleForm.name}
                    onChange={(e) => setSingleForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Display name"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Product'}
                  </button>
                </div>
              </form>
            ) : (
              /* Batch Entry Form */
              <form onSubmit={handleBatchSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Size Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={batchForm.size_category_id}
                      onChange={(e) => setBatchForm(f => ({ ...f, size_category_id: e.target.value, tile_type_id: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      required
                    >
                      <option value="">Select size</option>
                      {sizeCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tile Type
                    </label>
                    <select
                      value={batchForm.tile_type_id}
                      onChange={(e) => setBatchForm(f => ({ ...f, tile_type_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={!batchForm.size_category_id}
                    >
                      <option value="">{batchForm.size_category_id ? 'Select type' : 'Select size first'}</option>
                      {filteredTileTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tile Codes <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={batchForm.raw_input}
                    onChange={(e) => {
                      setBatchForm(f => ({ ...f, raw_input: e.target.value }));
                      setBatchResult(null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                    rows={5}
                    placeholder="Enter tile codes separated by comma or hyphen:
12201, 12202, 12204, 12205
- 44VT003
- 44VT004
- 44VT005"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Supports comma-separated (12201, 12202) or hyphen-separated (- 44VT003 - 44VT004)
                  </p>
                </div>

                {/* Preview */}
                {parsedCodes.length > 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Preview: {parsedCodes.length} code(s) found
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {parsedCodes.slice(0, 20).map((code, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs">
                          {code}
                        </span>
                      ))}
                      {parsedCodes.length > 20 && (
                        <span className="px-2 py-0.5 text-gray-500 text-xs">
                          +{parsedCodes.length - 20} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Result */}
                {batchResult && (
                  <div className={`p-4 rounded-lg border ${
                    batchResult.created > 0 && batchResult.errors?.length === 0 
                      ? 'bg-green-50 border-green-200' 
                      : batchResult.created > 0 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-red-50 border-red-200'
                  }`}>
                    {/* Success/Summary */}
                    <div className="flex items-center gap-2">
                      {batchResult.created > 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      )}
                      <span className={`font-semibold ${
                        batchResult.created > 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {batchResult.created > 0 
                          ? `${batchResult.created} product${batchResult.created > 1 ? 's' : ''} added successfully!`
                          : 'No products were added'
                        }
                      </span>
                    </div>

                    {/* Errors */}
                    {batchResult.errors?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-amber-200">
                        <p className="text-sm font-medium text-amber-800 mb-2">
                          {batchResult.errors.length} tile code{batchResult.errors.length > 1 ? 's' : ''} already exist{batchResult.errors.length === 1 ? 's' : ''}:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {batchResult.errors.slice(0, 10).map((err, i) => {
                            // Extract just the tile code from the error message
                            const match = err.match(/"([^"]+)"/);
                            const code = match ? match[1] : err;
                            return (
                              <span 
                                key={i} 
                                className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-mono"
                              >
                                {code}
                              </span>
                            );
                          })}
                          {batchResult.errors.length > 10 && (
                            <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-xs">
                              +{batchResult.errors.length - 10} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    disabled={loading}
                  >
                    {batchResult ? 'Close' : 'Cancel'}
                  </button>
                  {!batchResult && (
                    <button
                      type="submit"
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                      disabled={loading || parsedCodes.length === 0}
                    >
                      {loading ? 'Adding...' : `Add ${parsedCodes.length} Product(s)`}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCompanyProductModal;
